import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../../lib/hooks/useTheme';

interface DarkModeToggleProps {
  variant?: 'icon' | 'button';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showLabel?: boolean;
}

const DarkModeToggle = ({
  variant = 'icon',
  size = 'md',
  className = '',
  showLabel = false,
}: DarkModeToggleProps) => {
  const { theme, toggleTheme } = useTheme();

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  if (variant === 'button') {
    return (
      <motion.button
        onClick={toggleTheme}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-all duration-200 ${className}`}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <motion.div
          animate={{
            rotate: theme === 'dark' ? 180 : 0,
            scale: theme === 'dark' ? 1.1 : 1,
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {theme === 'dark' ? (
            <Sun size={iconSizes[size]} className="text-yellow-500" />
          ) : (
            <Moon size={iconSizes[size]} className="text-gray-600" />
          )}
        </motion.div>
        {showLabel && (
          <span className="text-sm font-medium">
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </span>
        )}
      </motion.button>
    );
  }

  return (
    <motion.button
      onClick={toggleTheme}
      className={`${sizeClasses[size]} flex items-center justify-center rounded-full border border-gray-200/30 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20 shadow-sm ${className}`}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        initial={false}
        animate={{
          rotate: theme === 'dark' ? 180 : 0,
          scale: theme === 'dark' ? 1.1 : 1,
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {theme === 'dark' ? (
          <Sun size={iconSizes[size]} className="text-yellow-400" />
        ) : (
          <Moon size={iconSizes[size]} className="text-neutral-700" />
        )}
      </motion.div>
    </motion.button>
  );
};

export default DarkModeToggle;
